import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { QueryState } from '@/components/QueryState'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { useMay } from '@/features/auth/access'
import {
  currentPeriod,
  formatMoney,
  getPayrollPreview,
  periodLabel,
  recompute,
  type RecomputeResult,
} from '@/features/commissions/api'
import { listLocations } from '@/features/orders/api'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'mt-1 h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

/**
 * The month-end check, and the decision that follows it.
 *
 * Reporting is safe and runs on demand. Applying is not: a difference means somebody edited
 * a rate card after the period was accrued, and whether last month is restated at the new
 * rate is a finance decision rather than a consequence of opening this screen. So the apply
 * button only appears once a report has actually found something, and it says how much money
 * it is about to move.
 */
function Recompute({ period }: { period: string }) {
  const queryClient = useQueryClient()
  const mayApply = useMay({ resource: 'commissions', action: 'update' })
  const [result, setResult] = useState<RecomputeResult | null>(null)

  const run = useMutation({
    mutationFn: (apply: boolean) => recompute(period, apply),
    onSuccess: (data) => {
      setResult(data)
      if (data.applied) {
        toast.success(`${data.differences.length} accrual(s) restated.`)
        void queryClient.invalidateQueries({ queryKey: ['payroll-preview'] })
        void queryClient.invalidateQueries({ queryKey: ['commission-statement'] })
      }
    },
    onError: (error: Error) => toast.error(error.message),
  })

  if (!mayApply) return null

  const differences = result?.differences ?? []

  return (
    <Card>
      <CardHeader
        title="Recompute the month"
        description="Re-runs every accrual in the period against today's rules and reports what would change. Nothing moves until you say so."
      />
      <div className="space-y-4 p-5">
        <Button
          variant="secondary"
          loading={run.isPending && !run.variables}
          onClick={() => run.mutate(false)}
        >
          Check {periodLabel(period)}
        </Button>

        {result && differences.length === 0 && (
          <Alert tone="success" title="Everything matches">
            {result.matched} of {result.accruals_checked} accruals recomputed to the same figure.
            Nothing to restate.
          </Alert>
        )}

        {differences.length > 0 && (
          <>
            <Alert tone="error" title={`${differences.length} accrual(s) would change`}>
              A rate card has been edited since these were accrued. Applying rewrites the amount and
              the breakdown; it does not re-approve anything, and any ledger entry already posted
              stays as it is for finance to correct.
            </Alert>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">Accruals that would change</caption>
                <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-2 py-2 font-medium">Invoice</th>
                    <th className="px-2 py-2 text-right font-medium">Accrued</th>
                    <th className="px-2 py-2 text-right font-medium">Recomputed</th>
                    <th className="px-2 py-2 text-right font-medium">Difference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {differences.map((row) => (
                    <tr key={row.accrual_id}>
                      <td className="px-2 py-2 font-medium">{row.invoice_number ?? '—'}</td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {formatMoney(row.stored_amount)}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {formatMoney(row.recomputed_amount)}
                      </td>
                      <td
                        className={`px-2 py-2 text-right font-medium tabular-nums ${
                          Number(row.difference) < 0 ? 'text-danger' : 'text-success'
                        }`}
                      >
                        {formatMoney(row.difference)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!result?.applied && (
              <Button
                variant="danger"
                loading={run.isPending && run.variables === true}
                onClick={() => run.mutate(true)}
              >
                Restate these {differences.length} accrual(s)
              </Button>
            )}
          </>
        )}
      </div>
    </Card>
  )
}

/**
 * The draft pay run: salary from the HR record, commission from what became payable.
 *
 * A preview, not a run. Nothing is written, no period is closed and nobody is paid — this
 * is the seam the brief asks for, showing commission feeding into payroll without a payroll
 * module to feed it into.
 *
 * The month here is the month the commission became *payable*, not the month it was sold.
 * Under a collection policy an August invoice settled in October is October's money, and
 * showing it in August would be a total nobody is about to transfer.
 */
export function PayrollPreview() {
  const [period, setPeriod] = useState(currentPeriod())
  const [locationId, setLocationId] = useState('')

  const { data: locationsPage } = useQuery({
    queryKey: ['locations'],
    queryFn: () => listLocations(),
    retry: false,
  })
  const locations = locationsPage?.data ?? []

  const { data, isLoading, error } = useQuery({
    queryKey: ['payroll-preview', period, locationId],
    queryFn: () => getPayrollPreview(period, locationId || undefined),
    retry: false,
  })

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Draft payroll run</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Salary plus the commission that became payable this month. A preview — nothing is paid
            and no period is closed.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Pay month
            </span>
            <Input
              type="month"
              className="mt-1 w-auto"
              value={period}
              onChange={(event) => setPeriod(event.target.value || currentPeriod())}
            />
          </label>
          <label className="text-sm">
            <span className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Location
            </span>
            <select
              className={SELECT_CLASS}
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
            >
              <option value="">Any location</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      <Card>
        <CardHeader
          title={periodLabel(period)}
          description="A rep with no HR record still earns commission, and appears here with a blank salary rather than being left out."
        />
        <QueryState
          isLoading={isLoading}
          error={error}
          isEmpty={data?.lines.length === 0}
          emptyMessage="No commission became payable in this month."
        >
          {data && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <caption className="sr-only">Draft payroll lines</caption>
                  <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                    <tr>
                      <th className="px-5 py-2 font-medium">Employee</th>
                      <th className="px-5 py-2 font-medium">Code</th>
                      <th className="px-5 py-2 text-right font-medium">Base salary</th>
                      <th className="px-5 py-2 text-right font-medium">Commission</th>
                      <th className="px-5 py-2 text-right font-medium">Gross</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {data.lines.map((line) => (
                      <tr key={line.user_id}>
                        <td className="px-5 py-2 font-medium">{line.name ?? '—'}</td>
                        <td className="px-5 py-2 font-mono text-xs text-slate-500 dark:text-slate-400">
                          {line.employee_code ?? 'No HR record'}
                        </td>
                        <td className="px-5 py-2 text-right tabular-nums">
                          {line.base_salary === null ? (
                            <span className="text-slate-400">—</span>
                          ) : (
                            formatMoney(line.base_salary)
                          )}
                        </td>
                        <td className="px-5 py-2 text-right tabular-nums">
                          {formatMoney(line.commission_amount)}
                        </td>
                        <td className="px-5 py-2 text-right font-medium tabular-nums">
                          {formatMoney(line.gross_amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-8 border-t border-slate-200 px-5 py-3 text-sm font-medium dark:border-slate-800">
                <span className="text-slate-500 dark:text-slate-400">Totals</span>
                <span className="tabular-nums">Base {formatMoney(data.total_base)}</span>
                <span className="tabular-nums">
                  Commission {formatMoney(data.total_commission)}
                </span>
                <span className="tabular-nums">Gross {formatMoney(data.total_gross)}</span>
              </div>
            </>
          )}
        </QueryState>
      </Card>

      <Recompute period={period} />
    </div>
  )
}
